import { useParams, Link, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    if (slug && !articles.find((a) => a.slug === slug)) {
      navigate('/category/chise-jingling', { replace: true });
    }
  }, [slug, navigate]);

  if (!article) {
    return null;
  }

  const category = categories.find((c) => c.slug === article.categorySlug);
  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 3);

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        "headline": article.title,
        "description": article.summary,
        "author": {
          "@type": "Organization",
          "name": article.author
        },
        "datePublished": article.publishDate,
        "inLanguage": "zh-CN",
        "articleSection": article.category,
        "keywords": article.tags.join(",")
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "首页",
            "item": "https://haosejingling.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "冒险篇章",
            "item": "https://haosejingling.com/category/chise-jingling"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": article.category,
            "item": `https://haosejingling.com/category/${article.categorySlug}`
          },
          {
            "@type": "ListItem",
            "position": 4,
            "name": article.title
          }
        ]
      }
    ]
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <ScrollToTop />
      <Navbar transparent={false} />
      <main className="pt-16">
        <article className="px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '冒险篇章', to: '/category/chise-jingling' },
                { label: article.category, to: `/category/${article.categorySlug}` },
                { label: article.title },
              ]}
            />

            <header className="mb-8">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium hover:bg-accent-200 transition-colors cursor-pointer"
                >
                  {article.category}
                </Link>
                <span className="text-xs text-foreground-400">{article.readTime}</span>
              </div>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-3 text-sm text-foreground-500">
                <span>{article.author}</span>
                <span>·</span>
                <span>{article.publishDate}</span>
              </div>
            </header>

            <div className="relative w-full h-56 md:h-80 rounded-lg overflow-hidden mb-8">
              <img
                src={article.imageUrl}
                alt={`好色精灵探访记 - ${article.title}`}
                title={`好色精灵探访记 ${article.title}`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.contentHtml }}
            />

            <div className="mt-8 pt-6 border-t border-background-200">
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
                更多{article.category}篇章
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.slug}`}
                    className="group bg-background-50 rounded-lg overflow-hidden hover:bg-background-200/50 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative h-40 overflow-hidden">
                      <img
                        src={ra.imageUrl}
                        alt={`好色精灵探访记 - ${ra.title}`}
                        title={`好色精灵探访记 ${ra.title}`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {ra.title}
                      </h3>
                      <p className="text-xs text-foreground-500">{ra.readTime} · {ra.publishDate}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}